import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clapperboard, Plus } from 'lucide-react';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { usePermissions } from '../hooks/usePermissions';
import { useI18n } from '../contexts/I18nContext';
import { useActors, useClientIndustries, useCreateActor } from '../hooks/useActors';
import { useClients } from '../hooks/useClients';
import { Select } from '../components/ui/Select';
import { FadeIn, SkeletonCardGrid } from '../components/ui/Skeleton';
import { Avatar } from '../components/tasks/linear/Avatar';
import { ActorFormModal } from '../components/actors/ActorFormModal';
import { ConflictBadge } from '../components/actors/ConflictBadge';

/**
 * Actori (opt-in module, built for Genius Media): the people who appear in
 * the shoots, as cards like the team page, with the clients each one was used
 * at and a "⚠ Conflict" badge when two of them are competitors.
 */
export function ActorsPage() {
  const { t } = useI18n();
  const { can } = usePermissions();
  const [search, setSearch] = useState('');
  const [industryId, setIndustryId] = useState('');
  const [clientId, setClientId] = useState('');
  const [creating, setCreating] = useState(false);
  const debounced = useDebouncedValue(search.trim());
  const create = useCreateActor();

  const {
    data: actors,
    isLoading,
    isError,
  } = useActors({
    search: debounced || undefined,
    industryId: industryId || undefined,
    clientId: clientId || undefined,
  });
  const { data: industries } = useClientIndustries();
  const { data: clients } = useClients({ active: 'true', sort: 'name', limit: 100 });
  const filtered = Boolean(debounced || industryId || clientId);

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Clapperboard className="text-brand-600" size={24} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{t('actors.title')}</h1>
            <p className="text-sm text-gray-500">{t('actors.subtitle')}</p>
          </div>
        </div>
        {can('actors', 'canCreate') && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            <Plus size={15} />
            {t('actors.add')}
          </button>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="block sm:w-64">
          <span className="ui-field-label">{t('common.search')}</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('actors.search')}
            className="ui-input w-full"
          />
        </label>
        <Select
          label={t('actors.filter.industry')}
          clearable
          searchable
          value={industryId}
          onChange={setIndustryId}
          placeholder={t('actors.filter.allIndustries')}
          options={(industries ?? []).map((i) => ({ value: i.id, label: i.name }))}
          className="w-full sm:w-48"
        />
        <Select
          label={t('actors.filter.client')}
          clearable
          searchable
          value={clientId}
          onChange={setClientId}
          placeholder={t('actors.filter.allClients')}
          options={(clients?.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          className="w-full sm:w-56"
        />
      </div>

      <div className="mt-6">
        {isLoading ? (
          <SkeletonCardGrid />
        ) : isError ? (
          <p className="text-sm text-red-600">{t('common.loadError')}</p>
        ) : !actors || actors.length === 0 ? (
          <p className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
            {filtered ? t('actors.emptyFiltered') : t('actors.empty')}
          </p>
        ) : (
          <FadeIn>
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {actors.map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/actors/${a.id}`}
                    className="flex h-full gap-3 rounded-lg border border-gray-200 bg-card p-4 transition hover:border-brand-300 hover:shadow-sm"
                  >
                    <Avatar name={a.fullName} size={44} src={a.avatarUrl} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-semibold text-gray-900">{a.fullName}</span>
                        <ConflictBadge groups={a.conflicts} />
                      </div>
                      {(a.phone || a.email) && (
                        <p className="truncate text-xs text-gray-500">
                          {[a.phone, a.email].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {a.clients.length === 0 ? (
                          <span className="text-xs text-gray-400">{t('actors.noClients')}</span>
                        ) : (
                          a.clients.slice(0, 6).map((l) => (
                            <span
                              key={l.clientId}
                              title={l.industry?.name ?? undefined}
                              className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700"
                            >
                              {l.clientName}
                              {l.industry && (
                                <span className="text-gray-400">· {l.industry.name}</span>
                              )}
                            </span>
                          ))
                        )}
                        {a.clients.length > 6 && (
                          <span className="text-xs text-gray-500">+{a.clients.length - 6}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </FadeIn>
        )}
      </div>

      {creating && (
        <ActorFormModal
          submitting={create.isPending}
          onClose={() => setCreating(false)}
          onSubmit={async (input) => {
            await create.mutateAsync(input);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}
